/**
 * ReadAloudButton — text-to-speech.
 *
 * Primary path: OpenAI TTS (nova voice) via our backend `/api/tts/speak`,
 *   streamed back as MP3 and played with an <Audio> element.
 * Fallback path: browser `speechSynthesis` when the TTS API fails
 *   (offline, server down, 5xx, etc).
 *
 * Rules:
 *   - Never auto-plays. Only speaks on explicit user click.
 *   - Shows loading pill "LemonBot is speaking…" while the MP3 is fetching.
 *   - A dedicated Stop button appears while speaking/paused.
 *   - After audio finishes, the blob URL is kept in-memory so a second click
 *     becomes an instant "Replay" with NO re-fetch — until the student stops,
 *     the component unmounts, or the source text changes.
 *   - Optional `cta` label (e.g. "Listen to feedback") renders as a subtle
 *     inline caption next to the button so students know what it does.
 *     The CTA hides after the first listen (the "Replay" button takes over).
 */
import { useEffect, useRef, useState } from "react";
import { Volume2, Pause, Play, Square, Loader2, RotateCcw } from "lucide-react";
import axios from "axios";
import { useAccessibility } from "@/contexts/AccessibilityContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SYNTH_SUPPORTED =
  typeof window !== "undefined" && "speechSynthesis" in window;

// "idle" — nothing playing
// "loading" — fetching MP3 from backend
// "speaking" — audio playing (either <audio> or speechSynthesis)
// "paused" — user paused

function getAuthHeaders() {
  const token = typeof window !== "undefined"
    ? window.localStorage.getItem("token")
    : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ReadAloudButton({
  text,
  testId = "read-aloud-btn",
  language = "en-US",
  inline = false,
  className = "",
  cta = null, // e.g. "Listen to feedback" — subtle hint next to the button
}) {
  const { settings } = useAccessibility();
  const [mode, setMode] = useState("idle"); // "idle" | "loading" | "speaking" | "paused"
  // canReplay flips to true after a successful first playthrough while the
  // blob URL is still valid (and the text hasn't changed). It's the signal
  // that clicking again should replay without re-fetching.
  const [canReplay, setCanReplay] = useState(false);

  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);
  const blobTextRef = useRef(null); // the text the current blob was made for
  const synthUtterRef = useRef(null);
  const abortRef = useRef(null);

  const trimmed = (text || "").trim();

  // If the source text changes, any cached audio is stale — wipe it so the
  // next click fetches fresh audio for the new text.
  useEffect(() => {
    if (blobUrlRef.current && blobTextRef.current !== trimmed) {
      hardReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      hardReset();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    };
  }, []);

  const visible =
    (SYNTH_SUPPORTED || BACKEND_URL) && settings.readAloudEnabled && trimmed;
  if (!visible) return null;

  // Stops any in-flight playback WITHOUT revoking the cached blob —
  // used when pausing / ending naturally so Replay can still reuse the audio.
  function softStopPlayback() {
    try { abortRef.current?.abort(); } catch { /* ignore */ }
    abortRef.current = null;
    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    } catch { /* ignore */ }
    try {
      if (SYNTH_SUPPORTED) window.speechSynthesis.cancel();
    } catch { /* ignore */ }
    synthUtterRef.current = null;
  }

  // Full teardown: stops playback AND releases the cached blob. Use this when
  // the user hits Stop, the text changes, or the component unmounts.
  function hardReset() {
    softStopPlayback();
    try {
      if (audioRef.current) {
        audioRef.current.src = "";
      }
    } catch { /* ignore */ }
    audioRef.current = null;
    if (blobUrlRef.current) {
      try { URL.revokeObjectURL(blobUrlRef.current); } catch { /* ignore */ }
      blobUrlRef.current = null;
    }
    blobTextRef.current = null;
    setCanReplay(false);
  }

  // Attach standard onended/onerror handlers to an <audio> element so the UI
  // returns to idle + surfaces replay. Used by both first-play and replay.
  const wireAudioHandlers = (audio) => {
    audio.onended = () => {
      setMode("idle");
      // Keep the blob URL alive — next click = instant replay.
      if (blobUrlRef.current && blobTextRef.current === trimmed) {
        setCanReplay(true);
      }
    };
    audio.onerror = () => {
      // Playback failure — drop any cache and fall back to browser voice.
      hardReset();
      speakWithBrowser();
    };
  };

  // ---- browser fallback ----
  const speakWithBrowser = () => {
    if (!SYNTH_SUPPORTED) {
      setMode("idle");
      return;
    }
    try {
      window.speechSynthesis.cancel();
      const u = new window.SpeechSynthesisUtterance(trimmed);
      u.lang = language;
      u.rate = 1;
      u.pitch = 1;
      u.onend = () => setMode("idle");
      u.onerror = () => setMode("idle");
      synthUtterRef.current = u;
      window.speechSynthesis.speak(u);
      setMode("speaking");
    } catch {
      setMode("idle");
    }
  };

  // ---- primary: OpenAI TTS ----
  const fetchAndPlay = async () => {
    if (!BACKEND_URL) {
      speakWithBrowser();
      return;
    }
    setMode("loading");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const resp = await axios.post(
        `${API}/tts/speak`,
        { text: trimmed, language: (language || "en").split("-")[0] },
        {
          responseType: "blob",
          headers: getAuthHeaders(),
          signal: controller.signal,
          timeout: 20000,
        }
      );
      // If user stopped / text changed while loading, bail.
      if (abortRef.current !== controller) return;

      const blob = new Blob([resp.data], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      blobTextRef.current = trimmed;

      const audio = new Audio(url);
      audioRef.current = audio;
      wireAudioHandlers(audio);
      await audio.play();
      setMode("speaking");
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        setMode("idle");
        return;
      }
      // eslint-disable-next-line no-console
      console.warn("[ReadAloudButton] OpenAI TTS failed, falling back:", err?.message);
      speakWithBrowser();
    }
  };

  // Replay reuses the cached blob — zero network, zero new TTS spend.
  const replayFromCache = async () => {
    const url = blobUrlRef.current;
    if (!url || blobTextRef.current !== trimmed) {
      // Stale / missing cache — do a fresh fetch.
      hardReset();
      fetchAndPlay();
      return;
    }
    try {
      // Build a fresh Audio() to guarantee playback restarts from 0.
      const audio = new Audio(url);
      audioRef.current = audio;
      wireAudioHandlers(audio);
      setCanReplay(false);
      await audio.play();
      setMode("speaking");
    } catch {
      // If replay fails for any reason, fall back to a fresh fetch.
      hardReset();
      fetchAndPlay();
    }
  };

  const start = () => {
    if (canReplay) {
      replayFromCache();
      return;
    }
    // First-time play for this text.
    hardReset();
    fetchAndPlay();
  };

  const pauseOrResume = () => {
    if (mode === "idle") {
      start();
      return;
    }
    if (mode === "loading") {
      // Cancel the in-flight request.
      softStopPlayback();
      setMode("idle");
      return;
    }
    try {
      if (audioRef.current) {
        if (mode === "speaking") {
          audioRef.current.pause();
          setMode("paused");
        } else if (mode === "paused") {
          audioRef.current.play().then(() => setMode("speaking")).catch(() => setMode("idle"));
        }
        return;
      }
      if (SYNTH_SUPPORTED) {
        if (mode === "speaking") {
          window.speechSynthesis.pause();
          setMode("paused");
        } else if (mode === "paused") {
          window.speechSynthesis.resume();
          setMode("speaking");
        }
      }
    } catch {
      setMode("idle");
    }
  };

  const stop = () => {
    hardReset();
    setMode("idle");
  };

  const isLoading = mode === "loading";
  const isPlaying = mode === "speaking";
  const isPaused = mode === "paused";
  const isReplayReady = mode === "idle" && canReplay;

  const labelMain = isLoading
    ? "LemonBot is speaking…"
    : isPlaying
    ? "Pause reading"
    : isPaused
    ? "Resume reading"
    : isReplayReady
    ? "Replay"
    : "Read this aloud";

  const Icon = isLoading
    ? Loader2
    : isPlaying
    ? Pause
    : isPaused
    ? Play
    : isReplayReady
    ? RotateCcw
    : Volume2;

  const buttonText = isLoading
    ? "LemonBot is speaking…"
    : isPlaying
    ? "Pause"
    : isPaused
    ? "Resume"
    : isReplayReady
    ? "Replay"
    : "Read aloud";

  const wrap = inline
    ? "inline-flex items-center gap-1"
    : "inline-flex items-center gap-1.5";

  // Show the CTA only in the initial idle state (before first play). Once
  // Replay is available, the "Replay" button itself is the affordance.
  const showCta =
    Boolean(cta) && mode === "idle" && !canReplay;

  return (
    <span className={`${wrap} ${className}`}>
      <button
        type="button"
        onClick={pauseOrResume}
        aria-pressed={isPlaying || isPaused}
        aria-label={labelMain}
        title={labelMain}
        data-testid={testId}
        disabled={false}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold border transition-colors ${
          isLoading
            ? "bg-amber-50 border-amber-300 text-amber-700"
            : isPlaying || isPaused
            ? "bg-emerald-50 border-emerald-300 text-emerald-700"
            : isReplayReady
            ? "bg-indigo-50 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
        }`}
      >
        <Icon
          className={`w-3.5 h-3.5 ${isLoading ? "animate-spin" : ""}`}
          aria-hidden="true"
        />
        {!inline && <span>{buttonText}</span>}
      </button>

      {/* Subtle CTA (first-play only) — tells the student what the button does. */}
      {showCta && (
        <span
          className="text-[11px] text-gray-500 italic select-none"
          data-testid={`${testId}-cta`}
          aria-hidden="true"
        >
          {cta}
        </span>
      )}

      {(isLoading || isPlaying || isPaused) && (
        <button
          type="button"
          onClick={stop}
          aria-label="Stop reading"
          title="Stop reading"
          data-testid={`${testId}-stop`}
          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
        >
          <Square className="w-3.5 h-3.5" aria-hidden="true" />
          {!inline && <span className="ml-1">Stop</span>}
        </button>
      )}
    </span>
  );
}

export const isSpeechSynthesisSupported = () => SYNTH_SUPPORTED;
